import Autocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import PropTypes from "prop-types"
import { useEffect, useMemo, useState } from "react"

import { adminApi } from "../axios"
import { normalizeStaffListResponse } from "../hooks/useAdminPaginatedQuery"

const SEARCH_DEBOUNCE_MS = 300

export function formatEntityLabel(option) {
  if (!option) return ""
  if (option.label) return option.label
  const name = String(option.name || "").trim()
  const email = String(option.email || "").trim()
  if (name && email && name !== email) return `${name} (${email})`
  return name || email || (option.id != null ? `#${option.id}` : "")
}

/**
 * Server-side search for the admin pickers. Asks the API with `q` instead of
 * preloading a page of options, so nothing past the 100-row page cap is lost.
 */
export const ENTITY_SEARCH = {
  // Active concierges only: pickers assign work, filters can still take an id.
  concierge: (q) =>
    adminApi
      .fetchConcierges({ q, page: 1, page_size: 20, active: "true" })
      .then(({ data }) => normalizeStaffListResponse(data).results),
  account: (q) =>
    q.length < 2
      ? Promise.resolve([])
      : adminApi
          .searchAccounts(q)
          .then(({ data }) =>
            Array.isArray(data)
              ? data
              : normalizeStaffListResponse(data).results
          ),
  mls: (q) =>
    adminApi
      .fetchMlsList({ q, page: 1, page_size: 20, ordering: "name" })
      .then(({ data }) =>
        normalizeStaffListResponse(data).results.map((mls) => ({
          id: mls.id,
          label: mls.name,
        }))
      ),
}

/** Label for an id that came from the URL (no option object yet). */
export const ENTITY_RESOLVE = {
  concierge: (id) =>
    adminApi.fetchUser(id).then(({ data }) => formatEntityLabel(data)),
  account: (id) =>
    adminApi.fetchUser(id).then(({ data }) => formatEntityLabel(data)),
  mls: (id) => adminApi.fetchMlsDetail(id).then(({ data }) => data?.name),
}

/**
 * Typeahead for one user / concierge / MLS. `value` is an option object (or
 * null); `staticOptions` (e.g. "Has any board") are always listed first.
 */
export default function AdminEntityAutocomplete({
  entity,
  value,
  onChange,
  label,
  placeholder,
  staticOptions = [],
  multiple = false,
  size = "small",
  sx,
  helperText,
  disabled = false,
}) {
  const [inputValue, setInputValue] = useState("")
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  // Selecting an option fills the input with its label; searching for that
  // label would hide every other option on the next open.
  const selectedLabel = !multiple && value ? formatEntityLabel(value) : null
  const searchTerm = inputValue === selectedLabel ? "" : inputValue.trim()

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    const timer = window.setTimeout(() => {
      setLoading(true)
      ENTITY_SEARCH[entity](searchTerm)
        .then((results) => {
          if (!cancelled) setOptions(results || [])
        })
        .catch(() => {
          if (!cancelled) setOptions([])
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [entity, searchTerm, open])

  const allOptions = useMemo(() => {
    const query = searchTerm.toLowerCase()
    const matchingStatic = staticOptions.filter(
      (option) => !query || option.label.toLowerCase().includes(query)
    )
    return [...matchingStatic, ...options]
  }, [searchTerm, options, staticOptions])

  return (
    <Autocomplete
      size={size}
      multiple={multiple}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={allOptions}
      loading={loading}
      value={value}
      disabled={disabled}
      onChange={(_, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      getOptionLabel={formatEntityLabel}
      isOptionEqualToValue={(option, selected) =>
        String(option.id) === String(selected.id)
      }
      filterOptions={(x) => x}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
        />
      )}
    />
  )
}

AdminEntityAutocomplete.propTypes = {
  entity: PropTypes.oneOf(["concierge", "account", "mls"]).isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  staticOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  multiple: PropTypes.bool,
  size: PropTypes.string,
  sx: PropTypes.object,
  helperText: PropTypes.node,
  disabled: PropTypes.bool,
}
